import { MONTHS } from './dateLabels';
import { finiteOrNull } from './numbers';

// How long ago a company reported, for the "Since Earnings" column's elapsed
// indicator (issue #229).
//
// The column's percent answers "how far has it drifted from the post-report
// close"; on its own that reads identically whether the report was three days
// or three months ago. These helpers supply the missing half.

// Trading sessions elapsed since a stored earnings date.
//
// Two things this must not do:
//
//   * Round-trip the date through `new Date(str)` plus getMonth()/getDate().
//     `earnings_history.earnings_date` is a bare 'YYYY-MM-DD' for every row
//     earnings_cron recorded, and a date-only string parses as UTC midnight
//     while those getters report LOCAL time — so west of Greenwich every event
//     lands a day early and the count is off by one (the date-only rule in
//     CLAUDE.md, same class as parseMonthKey in ./webTraffic.js). We read the
//     date PREFIX with a regex instead, which also absorbs the legacy ISO-Z and
//     UTC-offset shapes that same column carries.
//   * Return 0 for a missing or unparseable date. 0 is a real claim ("reported
//     today") and must not be manufactured out of absence — those cases return
//     null so the caller drops the indicator rather than rendering a number.
//
// Counts weekdays in (eventDay, today] — sessions that have CLOSED since the
// report day itself, which is the span the drift percent actually covers (it is
// measured from the close AFTER the print). US market holidays are not
// excluded, so a count can overstate by one across a holiday week; that is well
// inside the precision this indicator claims, and the alternative is shipping a
// holiday calendar that then has to be maintained forever.
// The ONE place a stored earnings date is parsed and judged valid (issue #231).
//
// This used to be written twice — once here and once in formatEventDate — and
// the two copies had already drifted: only this one rejected a rolled-over date,
// so `2026-02-30` produced no elapsed chip while the hover confidently read
// "Reported Feb 30". Two hand-written copies of one rule silently disagreeing is
// the exact shape CLAUDE.md keeps flagging, so validity is now decided once and
// both consumers read the result.
//
// Returns { year, month, day, utc, date } or null. `month` is 1-based;
// `utc`/`date` are the UTC-midnight anchor, which callers need anyway.
export function parseEventDate(dateString) {
  if (!dateString || typeof dateString !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Date.UTC ROLLS OVER rather than failing ('2026-02-30' -> Mar 2, and note
  // 2026 is not a leap year so '2026-02-29' rolls too). Read the anchor back
  // and reject anything that did not survive the round trip, so we never report
  // a day the string did not actually name.
  const utc = Date.UTC(year, month - 1, day);
  const date = new Date(utc);
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;

  return { year, month, day, utc, date };
}

export function tradingSessionsSince(dateString, now = new Date()) {
  const event = parseEventDate(dateString);
  if (!event) return null;

  // Anchor "today" on the viewer's LOCAL calendar date, re-expressed as a UTC
  // midnight so both sides of the subtraction sit in one frame.
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

  const days = Math.round((todayUtc - event.utc) / 86400000);
  if (days < 0) return null; // a future date is not an elapsed span
  if (days === 0) return 0;

  // Whole weeks contribute exactly 5 sessions each; walk only the remainder.
  let sessions = Math.floor(days / 7) * 5;
  const firstDow = (event.date.getUTCDay() + 1) % 7; // the day AFTER the event
  for (let i = 0; i < days % 7; i += 1) {
    const dow = (firstDow + i) % 7;
    if (dow !== 0 && dow !== 6) sessions += 1;
  }
  return sessions;
}

// An elapsed-sessions label: "today", "1 bar", "23 bars" — or with another unit,
// "23 trading days" (the stock profile's wording). Returns null — not an empty
// string — when there is nothing to say, so the caller renders nothing at all.
export function sessionsLabel(sessions, unit = 'bar') {
  if (sessions === null) return null;
  if (sessions === 0) return 'today';
  return `${sessions} ${unit}${sessions === 1 ? '' : 's'}`;
}

// The last recorded report as every surface states it (issue #445): the
// table's Since Earnings cell and the stock profile's Last Report tile read
// this one object, so they cannot disagree about the date, the elapsed count,
// the move on the day or the move since.
//
// `reportedOn` is derived FROM the elapsed count, not beside it (issue #231):
// a valid-but-future date yields no count, and must then yield no date either.
export function lastReportSummary(
  { reactionDate, reactionPercent, sincePercent },
  now = new Date(),
) {
  const sessions = tradingSessionsSince(reactionDate, now);
  return {
    sessions,
    elapsed: sessionsLabel(sessions),
    reportedOn: sessions === null ? null : formatEventDate(reactionDate),
    reaction: finiteOrNull(reactionPercent),
    since: finiteOrNull(sincePercent),
  };
}

// "Aug 14" from a stored earnings date, for the cell's hover text.
//
// NOT formatDate() from components/utils.js: that one does
// `new Date(str).toLocaleDateString()`, and a date-only string parses as UTC
// midnight — which renders as the PREVIOUS day for every viewer west of
// Greenwich. Same trap as above, so the same fix: format from the parsed
// prefix, in UTC, and never let a local-time getter near it.

export function formatEventDate(dateString) {
  const event = parseEventDate(dateString);
  if (!event) return null;
  return `${MONTHS[event.month - 1]} ${event.day}`;
}

// The US-session calendar date ('YYYY-MM-DD') of a FULL timestamp, for feeding
// the prefix-based helpers above (issue #306).
//
// `earnings_transcripts.call_date` is UTC ISO, and any call after 8pm ET
// carries the NEXT day's UTC date — ORCL's 2026-09-11T00:17Z is a Sep 10 call.
// Reading its prefix would date every after-the-close call a day late, and on
// the evening itself put it "in the future", which drops the chip entirely.
// Sessions are an ET concept, so convert to ET first. Never pass a date-only
// string here: it has no time to convert, and would shift back a day.
const ET_DATE_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
});

export function easternSessionDate(timestamp) {
  if (!timestamp || typeof timestamp !== 'string') return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(ET_DATE_PARTS.formatToParts(date).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

// Do two stored dates describe the SAME quarterly event?
//
// The call-highlights panel pairs a transcript (keyed by `call_date`, a UTC ISO
// timestamp written by earnings-transcript-cron) with the reaction computed off
// an `earnings_history` row (a bare 'YYYY-MM-DD' written by earnings_cron).
// Two workers, two schedules: in the hours after a print the transcript exists
// before the event has been recorded, so the newest reaction still belongs to
// the PREVIOUS quarter. Rendering it beside this call's headline would
// attribute a three-month-old move to a call that just happened — the same
// "fields that must share a vintage" trap CLAUDE.md keeps flagging, except here
// the two vintages come from different tables and cannot be selected together.
//
// Both sides go through parseEventDate, so every shape that column carries
// (date-only, ISO-Z, UTC-offset) is compared on its date PREFIX in UTC and no
// local-time getter is involved. The tolerance exists because a call can run
// the day after the release; three days cannot reach an adjacent quarter.
export function isSameEarningsEvent(a, b, toleranceDays = 3) {
  const left = parseEventDate(a);
  const right = parseEventDate(b);
  if (!left || !right) return false;
  return Math.abs(left.utc - right.utc) <= toleranceDays * 86400000;
}
